import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { UserCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Card } from "@/components/ui/Card";

const profileSchema = z
  .object({
    nombre: z.string().min(2, "Mínimo 2 caracteres"),
    telefono: z.string().min(6, "Teléfono inválido").optional().or(z.literal("")),
    password: z.string().min(8, "Mínimo 8 caracteres").optional().or(z.literal("")),
    confirmPassword: z.string().optional().or(z.literal("")),
  })
  .refine((data) => !data.password || data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });
type ProfileForm = z.infer<typeof profileSchema>;

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const accessToken = useAuthStore((s) => s.accessToken);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { nombre: user?.nombre ?? "", telefono: user?.telefono ?? "", password: "", confirmPassword: "" },
  });

  async function onSubmit(data: ProfileForm) {
    try {
      await api.put("/auth/me", {
        nombre: data.nombre,
        ...(data.telefono ? { telefono: data.telefono } : {}),
        ...(data.password ? { password: data.password } : {}),
      });
      if (user && accessToken) setSession(accessToken, { ...user, nombre: data.nombre, telefono: data.telefono || user.telefono });
      reset({ nombre: data.nombre, telefono: data.telefono, password: "", confirmPassword: "" });
      toast.success("Cambios guardados");
    } catch {
      toast.error("No se pudieron guardar los cambios");
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
          <UserCircle className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Mi perfil</h1>
          <p className="text-ink-400">{user?.email}</p>
        </div>
      </div>

      <Card className="max-w-md">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Input id="nombre" label="Nombre" error={errors.nombre?.message} {...register("nombre")} />
          <Input
            id="telefono"
            label="Teléfono de contacto"
            placeholder="600 000 000"
            error={errors.telefono?.message}
            {...register("telefono")}
          />
          <PasswordInput
            id="password"
            label="Nueva contraseña (opcional)"
            placeholder="Dejar en blanco para no cambiarla"
            error={errors.password?.message}
            {...register("password")}
          />
          <PasswordInput
            id="confirmPassword"
            label="Confirmar nueva contraseña"
            placeholder="Repite la nueva contraseña"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword")}
          />
          <Button type="submit" disabled={isSubmitting} className="w-fit">
            Guardar cambios
          </Button>
        </form>
      </Card>
    </div>
  );
}
